import { useState, useCallback } from "react";
import { saveSysconfig, fetchSysconfig } from "../../../services/api/sysconfigService";
import {
  addDeviceToSysconfig,
  updateDeviceById,
  deleteDeviceById,
  ensureMeterIds,
  ensureDeviceIds,
} from "../domain/deviceMutations";

/**
 * Orchestration-only hook for device mutations.
 * Each operation follows: fetch → ensure IDs → domain mutation → save.
 * No local sysconfig/devices state — callers use reload() from useDevices.
 *
 * @param {{ storageAccount: string, microgridName: string, datasetName: string }} params
 */
export function useDeviceMutations({
  storageAccount,
  microgridName,
  datasetName,
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchLatest = useCallback(
    () => fetchSysconfig({ storageAccount, microgridName, datasetName }),
    [storageAccount, microgridName, datasetName],
  );

  const persist = useCallback(
    (updatedSysconfig) =>
      saveSysconfig({
        storageAccount,
        microgridName,
        datasetName,
        sysconfig: updatedSysconfig,
      }),
    [storageAccount, microgridName, datasetName],
  );

  const addDevice = useCallback(
    async (formValues, { meterId } = {}) => {
      setSaving(true);
      setError(null);
      try {
        let latest = await fetchLatest();
        latest = await ensureMeterIds(latest);
        latest = await ensureDeviceIds(latest);
        const targetMeterId = meterId || latest.Microgrid?.meters?.[0]?.id;
        const updated = await addDeviceToSysconfig(latest, formValues, {
          meterId: targetMeterId,
        });
        await persist(updated);
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [fetchLatest, persist],
  );

  const updateDevice = useCallback(
    async (deviceId, updates) => {
      setSaving(true);
      setError(null);
      try {
        const latest = await fetchLatest();
        const updated = await updateDeviceById(latest, deviceId, updates);
        await persist(updated);
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [fetchLatest, persist],
  );

  const deleteDevice = useCallback(
    async (deviceId) => {
      setSaving(true);
      setError(null);
      try {
        const latest = await fetchLatest();
        const updated = deleteDeviceById(latest, deviceId);
        await persist(updated);
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [fetchLatest, persist],
  );

  return {
    saving,
    error,
    addDevice,
    updateDevice,
    deleteDevice,
  };
}
